import isBase64 from 'validator/lib/isBase64';
import { z } from 'zod';

import objectPassthrough from '../../objectPassthrough';
import {
  WithUnknownValuePlaceholder,
  makeUnknownValuePlaceholderSchema,
} from '../../unknownValuePlaceholder';

import { LoginWebsiteProtocol, schemaLoginWebsiteProtocol } from './LoginWebsiteProtocol';

// Used as a placeholder for forward compatibility with new scope entry types
interface UnknownScopeEntry {
  kind: 'unknown';
}

const UnknownScopeEntry = objectPassthrough({
  kind: z.literal('unknown'),
});

interface WebsiteScopeEntry {
  readonly kind: 'website';
  readonly pattern: string;
  readonly protocol?: WithUnknownValuePlaceholder<LoginWebsiteProtocol, undefined>;
}

const WebsiteScopeEntry = objectPassthrough({
  kind: z.literal('website'),
  pattern: z.string(),
  protocol: makeUnknownValuePlaceholderSchema(schemaLoginWebsiteProtocol, undefined).optional(),
});

interface AndroidAppScopeEntry {
  readonly kind: 'androidApp';
  readonly appId: string;
  readonly signatureHash: string;
  readonly displayName: string;
}

const AndroidAppScopeEntry = objectPassthrough({
  kind: z.literal('androidApp'),
  appId: z.string(),
  signatureHash: z.string().refine(isBase64),
  displayName: z.string(),
});

interface WindowsDesktopAppScopeEntry {
  readonly kind: 'windowsDesktopApp';
  readonly exeName: string;
}

const WindowsDesktopAppScopeEntry = objectPassthrough({
  kind: z.literal('windowsDesktopApp'),
  exeName: z.string(),
});

type LoginScopeEntry =
  | UnknownScopeEntry
  | WebsiteScopeEntry
  | AndroidAppScopeEntry
  | WindowsDesktopAppScopeEntry;

const LoginScopeEntry = z.discriminatedUnion('kind', [
  UnknownScopeEntry,
  WebsiteScopeEntry,
  AndroidAppScopeEntry,
  WindowsDesktopAppScopeEntry,
]);

type LoginScopeEntryKind = LoginScopeEntry['kind'];

const LoginScopeEntryKind = z.union([
  z.literal('unknown'),
  z.literal('website'),
  z.literal('androidApp'),
  z.literal('windowsDesktopApp'),
]);

export {
  LoginScopeEntry,
  UnknownScopeEntry,
  WebsiteScopeEntry,
  AndroidAppScopeEntry,
  WindowsDesktopAppScopeEntry,
  LoginScopeEntryKind,
};
